import { RelBox } from '../../../api/ai.api.types';
import { GrayFrame } from './frameCapture';

export const extractPatch = (frame: GrayFrame, cx: number, cy: number, size: number): Uint8ClampedArray | null => {
    const half = size / 2;
    const x0 = Math.round(cx - half);
    const y0 = Math.round(cy - half);
    if (x0 < 0 || y0 < 0 || x0 + size > frame.width || y0 + size > frame.height) {
        return null;
    }
    const patch = new Uint8ClampedArray(size * size);
    for (let y = 0; y < size; y++) {
        const rowOffset = (y0 + y) * frame.width + x0;
        for (let x = 0; x < size; x++) {
            patch[y * size + x] = frame.data[rowOffset + x];
        }
    }
    return patch;
};

const sadAt = (patch: Uint8ClampedArray, size: number, frame: GrayFrame, x0: number, y0: number): number => {
    let sum = 0;
    for (let y = 0; y < size; y++) {
        const rowOffset = (y0 + y) * frame.width + x0;
        for (let x = 0; x < size; x++) {
            sum += Math.abs(patch[y * size + x] - frame.data[rowOffset + x]);
        }
    }
    return sum;
};

export const bestOffset = (
    patch: Uint8ClampedArray,
    patchSize: number,
    frame: GrayFrame,
    cx: number,
    cy: number,
    radius: number,
): { dx: number; dy: number; score: number } | null => {
    const half = patchSize / 2;
    const baseX = Math.round(cx - half);
    const baseY = Math.round(cy - half);
    if (baseX - radius < 0 || baseY - radius < 0 || baseX + patchSize + radius > frame.width || baseY + patchSize + radius > frame.height) {
        return null;
    }
    let best = { dx: 0, dy: 0, score: Infinity };
    for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
            const score = sadAt(patch, patchSize, frame, baseX + dx, baseY + dy);
            if (score < best.score) {
                best = { dx, dy, score };
            }
        }
    }
    return best;
};

// Zero-mean normalized cross-correlation match. Same brute-force search as bestOffset, but the
// score is illumination-invariant (mobile auto-exposure shifts brightness mid-pan, which inflates
// SAD scores and kills keypoints). Returns the offset with the HIGHEST correlation in [-1..1].
export const bestOffsetNcc = (
    patch: Uint8ClampedArray,
    patchSize: number,
    frame: GrayFrame,
    cx: number,
    cy: number,
    radius: number,
): { dx: number; dy: number; score: number } | null => {
    const half = patchSize / 2;
    const baseX = Math.round(cx - half);
    const baseY = Math.round(cy - half);
    if (baseX - radius < 0 || baseY - radius < 0 || baseX + patchSize + radius > frame.width || baseY + patchSize + radius > frame.height) {
        return null;
    }
    const n = patchSize * patchSize;
    // Precompute the zero-mean patch once; then Σ zp·f is the correlation numerator because Σ zp = 0.
    let patchSum = 0;
    for (let i = 0; i < n; i++) {
        patchSum += patch[i];
    }
    const patchMean = patchSum / n;
    const zeroMeanPatch = new Float32Array(n);
    let patchVar = 0;
    for (let i = 0; i < n; i++) {
        const d = patch[i] - patchMean;
        zeroMeanPatch[i] = d;
        patchVar += d * d;
    }
    if (patchVar < 1e-6) {
        return null; // flat template — correlation undefined
    }

    let best = { dx: 0, dy: 0, score: -Infinity };
    for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
            const x0 = baseX + dx;
            const y0 = baseY + dy;
            let sumF = 0;
            let sumF2 = 0;
            let cross = 0;
            for (let y = 0; y < patchSize; y++) {
                const rowOffset = (y0 + y) * frame.width + x0;
                const patchRow = y * patchSize;
                for (let x = 0; x < patchSize; x++) {
                    const f = frame.data[rowOffset + x];
                    sumF += f;
                    sumF2 += f * f;
                    cross += zeroMeanPatch[patchRow + x] * f;
                }
            }
            const frameVar = sumF2 - (sumF * sumF) / n;
            const score = frameVar > 1e-6 ? cross / Math.sqrt(patchVar * frameVar) : 0;
            if (score > best.score) {
                best = { dx, dy, score };
            }
        }
    }
    return best;
};

const bilinearAt = (frame: GrayFrame, x: number, y: number): number => {
    const x0 = Math.floor(x);
    const y0 = Math.floor(y);
    const fx = x - x0;
    const fy = y - y0;
    const i = y0 * frame.width + x0;
    const a = frame.data[i];
    const b = frame.data[i + 1];
    const c = frame.data[i + frame.width];
    const d = frame.data[i + frame.width + 1];
    return a * (1 - fx) * (1 - fy) + b * fx * (1 - fy) + c * (1 - fx) * fy + d * fx * fy;
};

const KLT_MAX_ITERATIONS = 4;
const KLT_CONVERGENCE_PX = 0.05;
const KLT_MAX_REFINE_DIST_PX = 3;

// Lucas-Kanade sub-pixel refinement (Gauss-Newton on the template patch). Starts from an integer
// match and iterates ∇T-weighted error against bilinear frame samples. Returns the refined center
// or null when the system is degenerate / the solution runs away — callers fall back to the
// integer match. Sub-pixel results make the consensus (and thus the box) noticeably smoother than
// the 1px-quantized block match alone.
export const kltRefine = (
    patch: Uint8ClampedArray,
    patchSize: number,
    frame: GrayFrame,
    startCx: number,
    startCy: number,
): { x: number; y: number } | null => {
    // Template gradients over interior pixels + constant Hessian.
    const gx = new Float32Array(patchSize * patchSize);
    const gy = new Float32Array(patchSize * patchSize);
    let sxx = 0;
    let sxy = 0;
    let syy = 0;
    for (let y = 1; y < patchSize - 1; y++) {
        for (let x = 1; x < patchSize - 1; x++) {
            const i = y * patchSize + x;
            const dgx = (patch[i + 1] - patch[i - 1]) / 2;
            const dgy = (patch[i + patchSize] - patch[i - patchSize]) / 2;
            gx[i] = dgx;
            gy[i] = dgy;
            sxx += dgx * dgx;
            sxy += dgx * dgy;
            syy += dgy * dgy;
        }
    }
    const det = sxx * syy - sxy * sxy;
    if (det < 1e-3) {
        return null; // aperture problem — no unique solution
    }

    const half = patchSize / 2;
    let cx = startCx;
    let cy = startCy;
    for (let iter = 0; iter < KLT_MAX_ITERATIONS; iter++) {
        let bx = 0;
        let by = 0;
        for (let y = 1; y < patchSize - 1; y++) {
            for (let x = 1; x < patchSize - 1; x++) {
                const fx = cx - half + x;
                const fy = cy - half + y;
                if (fx < 0 || fy < 0 || fx >= frame.width - 1 || fy >= frame.height - 1) {
                    return null;
                }
                const i = y * patchSize + x;
                const err = bilinearAt(frame, fx, fy) - patch[i];
                bx += gx[i] * err;
                by += gy[i] * err;
            }
        }
        const dx = -(syy * bx - sxy * by) / det;
        const dy = -(-sxy * bx + sxx * by) / det;
        cx += dx;
        cy += dy;
        if (Math.hypot(cx - startCx, cy - startCy) > KLT_MAX_REFINE_DIST_PX) {
            return null; // diverging — trust the integer match instead
        }
        if (Math.hypot(dx, dy) < KLT_CONVERGENCE_PX) {
            break;
        }
    }
    return { x: cx, y: cy };
};

// Clamps how much a freshly adopted server box may change size relative to the previously
// tracked box, per edge, while keeping the box centered exactly where the (unclamped) target
// says it is. A single outlier detection (too wide/tall, typically near the frame edge) would
// otherwise yank the overlay right when local tracking is weakest; real size changes converge
// within a few responses, so clamping does not prevent them, only smooths a single spike.
export const clampAdoptedBoxSize = (previous: RelBox, target: RelBox, maxEdgeFactor: number): RelBox => {
    const centerX = target.x + target.w / 2;
    const centerY = target.y + target.h / 2;
    const minW = previous.w / maxEdgeFactor;
    const maxW = previous.w * maxEdgeFactor;
    const minH = previous.h / maxEdgeFactor;
    const maxH = previous.h * maxEdgeFactor;
    const w = Math.min(maxW, Math.max(minW, target.w));
    const h = Math.min(maxH, Math.max(minH, target.h));
    return { x: centerX - w / 2, y: centerY - h / 2, w, h };
};

// --- Keypoint tracking (Shi-Tomasi-style corner selection + per-point block matching) ---------
//
// A single center patch cannot track a meter: its center is usually a low-texture surface where
// SAD matching is ambiguous (aperture problem) and silently drifts. Instead we pick the N most
// corner-like points inside the detection box (approximated Shi-Tomasi minimum eigenvalue — high
// gradient energy in BOTH directions, i.e. corners/digits/edges crossings, not flat areas or
// straight edges), track each one independently, and combine them into a robust median consensus.

export interface TrackerKeypoint {
    // Position in tracker-frame pixels.
    x: number;
    y: number;
    patch: Uint8ClampedArray;
    // Corner strength at selection time (λmin approximation) — kept for the debug overlay.
    cornerScore: number;
    alive: boolean;
}

export interface KeypointConsensus {
    dx: number;
    dy: number;
    scale: number;
    alive: number;
}

// Shi-Tomasi minimum-eigenvalue approximation of the structure tensor summed over a small window:
// λmin = trace/2 − sqrt((Δ/2)² + Ixy²). Flat areas score ~0, straight edges score low (only one
// strong gradient direction), corners/texture score high.
const cornerScoreAt = (frame: GrayFrame, cx: number, cy: number, win: number): number => {
    let ixx = 0;
    let iyy = 0;
    let ixy = 0;
    for (let dy = -win; dy <= win; dy++) {
        for (let dx = -win; dx <= win; dx++) {
            const x = cx + dx;
            const y = cy + dy;
            const gx = frame.data[y * frame.width + x + 1] - frame.data[y * frame.width + x - 1];
            const gy = frame.data[(y + 1) * frame.width + x] - frame.data[(y - 1) * frame.width + x];
            ixx += gx * gx;
            iyy += gy * gy;
            ixy += gx * gy;
        }
    }
    const half = (ixx + iyy) / 2;
    const diff = Math.sqrt(((ixx - iyy) / 2) ** 2 + ixy * ixy);
    return half - diff;
};

const CORNER_WINDOW = 2;
// Candidate raster inside each grid cell — every 2nd pixel keeps selection cheap enough to run
// on every re-anchor without a measurable frame hit.
const CANDIDATE_STEP = 2;

// Picks up to `count` corner-like keypoints inside `region` (tracker-frame pixels). A grid with
// one winner per cell enforces spatial spread, so the points cover the whole meter instead of
// clustering on the single strongest feature.
export const selectKeypoints = (
    frame: GrayFrame,
    region: { x: number; y: number; w: number; h: number },
    options: { count: number; patchSize: number; minScore: number },
    // Optional per-candidate veto, checked before a candidate is accepted (used by
    // selectReplenishKeypoints to reject candidates too close to already-alive points, without
    // duplicating the corner-scoring/grid logic here).
    reject?: (candidate: { x: number; y: number }) => boolean,
): TrackerKeypoint[] => {
    const { count, patchSize, minScore } = options;
    const margin = patchSize / 2 + CORNER_WINDOW + 1;
    const x0 = Math.max(Math.round(region.x), margin);
    const y0 = Math.max(Math.round(region.y), margin);
    const x1 = Math.min(Math.round(region.x + region.w), frame.width - margin);
    const y1 = Math.min(Math.round(region.y + region.h), frame.height - margin);
    if (x1 - x0 < CANDIDATE_STEP || y1 - y0 < CANDIDATE_STEP) {
        return [];
    }

    const grid = Math.max(2, Math.ceil(Math.sqrt(count)) + 1);
    const cellW = (x1 - x0) / grid;
    const cellH = (y1 - y0) / grid;

    const cellBest: Array<{ x: number; y: number; score: number }> = [];
    for (let gy = 0; gy < grid; gy++) {
        for (let gx = 0; gx < grid; gx++) {
            let best: { x: number; y: number; score: number } | null = null;
            const cx0 = Math.round(x0 + gx * cellW);
            const cx1 = Math.round(x0 + (gx + 1) * cellW);
            const cy0 = Math.round(y0 + gy * cellH);
            const cy1 = Math.round(y0 + (gy + 1) * cellH);
            for (let y = cy0; y < cy1; y += CANDIDATE_STEP) {
                for (let x = cx0; x < cx1; x += CANDIDATE_STEP) {
                    const score = cornerScoreAt(frame, x, y, CORNER_WINDOW);
                    if (score >= minScore && (!best || score > best.score)) {
                        best = { x, y, score };
                    }
                }
            }
            if (best) {
                cellBest.push(best);
            }
        }
    }

    cellBest.sort((a, b) => b.score - a.score);

    const keypoints: TrackerKeypoint[] = [];
    for (const candidate of cellBest) {
        if (keypoints.length >= count) {
            break;
        }
        if (reject?.(candidate)) {
            continue;
        }
        const patch = extractPatch(frame, candidate.x, candidate.y, patchSize);
        if (patch) {
            keypoints.push({ x: candidate.x, y: candidate.y, patch, cornerScore: candidate.score, alive: true });
        }
    }
    return keypoints;
};

// Like selectKeypoints, but for topping up an already-partially-alive keypoint set: candidates
// closer than `minDistance` to a LIVING existing point are rejected (they would duplicate an
// already-tracked feature); dead points impose no such exclusion zone since that scene position
// is no longer covered by anything. Reuses selectKeypoints' candidate scoring/grid via its reject
// hook instead of duplicating the corner-selection logic.
export const selectReplenishKeypoints = (
    frame: GrayFrame,
    region: { x: number; y: number; w: number; h: number },
    existing: TrackerKeypoint[],
    options: { count: number; patchSize: number; minScore: number; minDistance: number },
): TrackerKeypoint[] => {
    const { minDistance, ...selectOptions } = options;
    const alive = existing.filter((kp) => kp.alive);
    const tooClose = (candidate: { x: number; y: number }): boolean =>
        alive.some((kp) => Math.hypot(kp.x - candidate.x, kp.y - candidate.y) < minDistance);
    return selectKeypoints(frame, region, selectOptions, tooClose);
};

// Selectable per-point matcher: 'sad' = plain block matching (fast, integer), 'ncc' = normalized
// cross-correlation (illumination-invariant, integer), 'klt' = SAD initialization + Lucas-Kanade
// sub-pixel refinement (smoothest). The consensus logic is identical for all three, so switching
// compares the matchers fairly.
export type TrackerAlgorithm = 'sad' | 'ncc' | 'klt';

// A match whose mean per-pixel SAD exceeds this is considered lost (occluded / left the frame /
// changed too much) and kills the keypoint instead of feeding garbage into the consensus.
const MAX_MEAN_SAD = 25;
// NCC matches correlating worse than this are considered lost.
const MIN_NCC_SCORE = 0.5;
// Matches farther than this from the median displacement are outliers (point latched onto the
// background or another object) and are killed as well.
const OUTLIER_MAX_DIST_PX = 3;
// Per-tick scale clamp: between two video frames the meter cannot grow/shrink by more than a few
// percent; anything larger is consensus noise.
const SCALE_CLAMP = 0.05;
const MIN_CONSENSUS_POINTS = 3;
const MIN_POINTS_FOR_SCALE = 4;

const median = (values: number[]): number => {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

// Tracks all living keypoints in the new frame and reduces them to a median-consensus
// translation plus a clamped uniform scale. Mutates the keypoints in place (positions advance,
// failed/outlier points are marked dead). Returns null when fewer than MIN_CONSENSUS_POINTS
// survive — the caller should treat the target as lost and wait for the next server detection.
export const trackKeypoints = (
    frame: GrayFrame,
    keypoints: TrackerKeypoint[],
    patchSize: number,
    radius: number,
    algorithm: TrackerAlgorithm = 'sad',
): KeypointConsensus | null => {
    const matches: Array<{ kp: TrackerKeypoint; dx: number; dy: number }> = [];
    for (const kp of keypoints) {
        if (!kp.alive) {
            continue;
        }
        let dx: number | null = null;
        let dy: number | null = null;
        if (algorithm === 'ncc') {
            const offset = bestOffsetNcc(kp.patch, patchSize, frame, kp.x, kp.y, radius);
            if (offset && offset.score >= MIN_NCC_SCORE) {
                dx = offset.dx;
                dy = offset.dy;
            }
        } else {
            const offset = bestOffset(kp.patch, patchSize, frame, kp.x, kp.y, radius);
            if (offset && offset.score / (patchSize * patchSize) <= MAX_MEAN_SAD) {
                dx = offset.dx;
                dy = offset.dy;
                if (algorithm === 'klt') {
                    // Refine the integer match to sub-pixel; fall back to it when refinement fails.
                    const refined = kltRefine(kp.patch, patchSize, frame, kp.x + dx, kp.y + dy);
                    if (refined) {
                        dx = refined.x - kp.x;
                        dy = refined.y - kp.y;
                    }
                }
            }
        }
        if (dx == null || dy == null) {
            kp.alive = false;
            continue;
        }
        matches.push({ kp, dx, dy });
    }
    if (matches.length < MIN_CONSENSUS_POINTS) {
        return null;
    }

    const medianDx = median(matches.map((m) => m.dx));
    const medianDy = median(matches.map((m) => m.dy));
    const inliers = matches.filter((m) => {
        const isInlier = Math.hypot(m.dx - medianDx, m.dy - medianDy) <= OUTLIER_MAX_DIST_PX;
        if (!isInlier) {
            m.kp.alive = false;
        }
        return isInlier;
    });
    if (inliers.length < MIN_CONSENSUS_POINTS) {
        return null;
    }

    let scale = 1;
    if (inliers.length >= MIN_POINTS_FOR_SCALE) {
        const cx0 = inliers.reduce((s, m) => s + m.kp.x, 0) / inliers.length;
        const cy0 = inliers.reduce((s, m) => s + m.kp.y, 0) / inliers.length;
        const cx1 = inliers.reduce((s, m) => s + m.kp.x + m.dx, 0) / inliers.length;
        const cy1 = inliers.reduce((s, m) => s + m.kp.y + m.dy, 0) / inliers.length;
        const spread0 = inliers.reduce((s, m) => s + Math.hypot(m.kp.x - cx0, m.kp.y - cy0), 0) / inliers.length;
        const spread1 = inliers.reduce((s, m) => s + Math.hypot(m.kp.x + m.dx - cx1, m.kp.y + m.dy - cy1), 0) / inliers.length;
        if (spread0 > 1e-3) {
            scale = Math.min(1 + SCALE_CLAMP, Math.max(1 - SCALE_CLAMP, spread1 / spread0));
        }
    }

    for (const m of inliers) {
        m.kp.x += m.dx;
        m.kp.y += m.dy;
    }

    return {
        dx: median(inliers.map((m) => m.dx)),
        dy: median(inliers.map((m) => m.dy)),
        scale,
        alive: inliers.length,
    };
};
